import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import store from '../redux/store';
import { get_user_data, set_open_log_in } from '../redux/Slice/userSlice';
import { iconsPath, imagePath } from '../utils/imagePath';
import { validateFunction } from '../utils/UtilsFunction';
import ButtonComponent from './ui/ButtonComponent';
import Divider from './ui/Divider';
import Input from './ui/Input';
import { useDispatch } from 'react-redux';

const LoginPage = ({ closeModal }) => {
  const [step, setStep] = useState(1);
  const user_data = JSON.parse(localStorage.getItem('user'));
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(set_open_log_in(false));
  }, []);
  // const [isChecked, setIsChecked] = useState(true);
  // const [selectedOption, setSelectedOption] = useState('');
  const [loading, setLoading] = useState(false);
  const [addData, setAddData] = useState({});
  const navigate = useNavigate();
  const passwordRef = useRef(null);

  useEffect(() => {
    if (user_data) {
      store.dispatch(get_user_data(user_data));
      setStep(1);
      navigate('/dashboard');
    }
  }, [user_data]);

  // Handle Google login
  const googleLogin = () => {
    window.location.href = `${process.env.REACT_APP_BASE_URL}users/auth/google`;
  };

  // Check if user is registered
  const checkUser = async (e) => {
    e.preventDefault();
    const errors = validateFunction(addData, ['email']);
    if (Object.keys(errors).length === 0) {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}users/check-if-registered`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: addData?.email }),
          }
        );
        const res = await response.json();
        // res.success
        //   ? setAddData((prev) => ({ ...prev, showPass: true }))
        //   : toast.error(res?.message, { id: 1 });
        if (res.success) {
          setAddData((prev) => ({
            ...prev,
            showPass: true,
            verifiedEmail: prev.email,
          }));

          // Focus on the password field after state update
          setTimeout(() => {
            passwordRef.current?.focus();
          }, 100);
        } else {
          toast.error(res?.message, { id: 1 });
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    } else {
      setAddData((prev) => ({ ...prev, ...errors }));
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    // For login, only validate email format and that password is non-empty.
    // Do NOT use the password complexity regex — that's for registration only.
    const errors = validateFunction(addData, ['email']);
    if (!addData?.password || String(addData.password).trim() === '') {
      errors['password_err'] = 'password is required';
    }
    if (Object.keys(errors).length === 0) {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}users/login`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: addData?.email,
              password: addData?.password,
            }),
          }
        );
        const res = await response.json();
        if (res.success) {
          localStorage.setItem('user', JSON.stringify(res.data));
          localStorage.setItem('token', res.data.accessToken);
          store.dispatch(get_user_data(res.data));
          // dispatch(set_open_log_in(false));
          // closeModal();
          navigate('/dashboard');
        } else {
          toast.error(res?.message, { id: 1 });
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    } else {
      setAddData((prev) => ({ ...prev, ...errors }));
    }
  };

  // Handle input change
  const handleChange = useCallback((value, key) => {
    setAddData((prev) => {
      const next = { ...prev, [key]: value, [`${key}_err`]: '' };

      if (
        key === 'email' &&
        prev.showPass &&
        prev.verifiedEmail !== undefined &&
        value !== prev.verifiedEmail
      ) {
        return {
          ...next,
          showPass: false,
          password: '',
          password_err: '',
          verifiedEmail: undefined,
        };
      }

      return next;
    });
  }, []);

  return (
    <div className="bg-[#1E1F22] mb-4 md:mb-0 p-4 md:p-8 rounded-lg shadow-xl  min-w-[350px] max-w-[350px] md:min-w-[450px] md:max-w-[450px] flex flex-col items-center gap-4  md:min-h-[500px]">
      <img
        src={imagePath?.logo_small}
        alt="logo_small"
        className="h-auto w-[80px] cursor-pointer"
        onClick={closeModal}
      />
      {step === 1 && (
        <>
          <h2 className="text-2xl font-semibold text-white">
            Welcome to{' '}
            <span className="text-[#FFFFFF] font-bold">Spatial Grid</span>
          </h2>
          <p className="text-sm text-gray-400 mb-2">
            Login with your email.
          </p>
          <ButtonComponent
            onClick={googleLogin} 
            className={`${
              loading ? '' : 'hover:scale-105'
            } w-full text-[18px] font-inter font-medium text-white !bg-[#4581FF] rounded-[10px] flex justify-center gap-3 py-3 px-10 items-center transform  transition duration-300`}
          >
            <img
              src={iconsPath.googleIcon}
              alt="menu icon"
              className="h-[23px] w-[23px] cursor-pointer"
            />{' '}
            Continue with Google
          </ButtonComponent>
          <Divider />
          <form
            className="w-full"
            onSubmit={addData?.showPass ? handleLogin : checkUser}
          >
            <Input
              // disabled={addData?.showPass}
              value={addData?.email}
              onChange={(e) => handleChange(e.target.value, 'email')}
              type="email"
              placeholder="Enter your email"
              // className="w-full bg-gray-700 text-white p-3 rounded-lg"
              error={addData?.email_err}
              className="min-w-full bg-[#4A4A50] text-center text-white rounded-[5px] p-3 mb-4 placeholder:text-[#8A8888] text-[15px] font-inter font-normal focus:outline-none"
            />
            {addData?.showPass && (
              <Input
                ref={passwordRef} // Attach ref here
                value={addData?.password}
                onChange={(e) => handleChange(e.target.value, 'password')}
                type="password"
                placeholder="Enter your password"
                className="min-w-full bg-[#4A4A50] text-center text-white rounded-[5px] p-3 mb-4 placeholder:text-[#8A8888] text-[15px] font-inter font-normal focus:outline-none"
                error={addData?.password_err}
              />
            )}
            <ButtonComponent className="w-full" loading={loading}>
              {addData?.showPass ? 'Login' : 'Continue'}
            </ButtonComponent>
          </form>
        </>
      )}
    </div>
  );
};

export default LoginPage;
